import React, { useEffect, useRef, useState } from 'react';
import { downloadAsset } from '../data/uploadService';
import { TRASH_RETENTION_DAYS } from '../data/firestore';
import Modal from './Modal';

// Inline name editor. Shows the name with a small pencil; clicking turns it
// into an input with Save / cancel. onSave(newName) does the persistence.
// Renaming only updates the display name — never the S3 key — so playback and
// existing movie clips are unaffected.
export function InlineRename({ name, onSave, className, style, title = 'Rename' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(name || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => setVal(name || ''), [name]);

  const stop = (e) => e.stopPropagation();
  const cancel = (e) => {
    stop(e);
    setVal(name || '');
    setEditing(false);
  };
  const commit = async (e) => {
    stop(e);
    const v = val.trim();
    if (!v || v === name) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(v);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <span className={`cs-rename ${className || ''}`} style={style}>
        <span className="cs-rename-text">{name}</span>
        <button
          type="button"
          className="cs-iconbtn"
          title={title}
          onClick={(e) => {
            stop(e);
            setEditing(true);
          }}
        >
          ✎
        </button>
      </span>
    );
  }

  return (
    <span className={`cs-rename cs-rename-editing ${className || ''}`} style={style} onClick={stop}>
      <input
        className="cs-input cs-input-sm"
        value={val}
        autoFocus
        disabled={busy}
        onChange={(e) => setVal(e.target.value)}
        onClick={stop}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(e);
          if (e.key === 'Escape') cancel(e);
        }}
      />
      <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={commit}>
        {busy ? '…' : 'Save'}
      </button>
      <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={cancel}>
        ✕
      </button>
    </span>
  );
}

// Download button that streams an S3 object straight to the browser with a
// friendly filename. `s3Key` is required; `bucket` is 'raw' (footage) or
// 'movies' (songs / derived assets).
export function DownloadButton({
  s3Key,
  name,
  bucket = 'raw',
  parts,
  label = '⤓ Download',
  title = 'Download original',
  className = 'cs-btn cs-btn-ghost cs-btn-sm',
  disabled,
}) {
  const [busy, setBusy] = useState(false);
  // A .RDC folder downloads every part file (kept as a set); a normal asset is
  // a single object. Sequential + staggered so the browser doesn't choke.
  const list = Array.isArray(parts) && parts.length ? parts : s3Key ? [{ s3Key, name }] : [];
  if (!list.length) return null;
  const multi = list.length > 1;
  const go = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      for (let i = 0; i < list.length; i += 1) {
        const p = list[i];
        // eslint-disable-next-line no-await-in-loop
        await downloadAsset({ key: p.s3Key, name: p.name, bucket });
        // eslint-disable-next-line no-await-in-loop
        if (i < list.length - 1) await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(`Download failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className={className}
      title={multi ? `Download all ${list.length} files in this .RDC folder` : title}
      disabled={busy || disabled}
      onClick={go}
    >
      {busy ? 'Preparing…' : multi ? `⤓ Download .RDC (${list.length})` : label}
    </button>
  );
}

// Irreversible-delete confirmation. The user drags the thumb all the way to the
// right; releasing it at the end fires the permanent delete immediately (a true
// slide-to-confirm — no second button to press). Released short of the end, it
// snaps back. Built on pointer events with a ref-backed position so the release
// handler always sees the latest value (no stale closure).
const VAPORIZE_THRESHOLD = 0.97;

export function VaporizeModal({ what = 'this item', onConfirm, onClose }) {
  const [pos, setPos] = useState(0); // 0..1 thumb position
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const draggingRef = useRef(false);
  const busyRef = useRef(false);

  const setPosBoth = (v) => {
    const c = Math.max(0, Math.min(1, v));
    posRef.current = c;
    setPos(c);
  };

  const fire = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err.message || String(err));
      busyRef.current = false;
      setBusy(false);
      setPosBoth(0);
    }
  };

  const posFromClientX = (clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const thumb = 46; // keep in sync with CSS thumb width
    const usable = Math.max(1, rect.width - thumb - 6);
    return (clientX - rect.left - thumb / 2 - 3) / usable;
  };

  const onPointerDown = (e) => {
    if (busyRef.current) return;
    draggingRef.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    setPosBoth(posFromClientX(e.clientX));
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setPosBoth(posFromClientX(e.clientX));
  };
  const onPointerUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (posRef.current >= VAPORIZE_THRESHOLD) {
      setPosBoth(1);
      fire();
    } else {
      setPosBoth(0);
    }
  };

  const pct = Math.round(pos * 100);
  return (
    <Modal title="Vaporize permanently?" onClose={busy ? () => {} : onClose} maxWidth={460}>
      <div className="cs-vaporize">
        <p className="cs-vaporize-warn">
          ⚠️ This permanently deletes <strong>{what}</strong> from storage right now.
          It will <strong>not</strong> go to the {TRASH_RETENTION_DAYS}-day recoverable
          trash and <strong>cannot be undone</strong>.
        </p>
        <div
          ref={trackRef}
          className={`cs-vaporize-confirm ${busy ? 'busy' : ''} ${pos >= 1 ? 'full' : ''}`}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <div className="cs-vaporize-confirm-fill" style={{ width: `calc(${pct}% )` }} />
          <span className="cs-vaporize-confirm-label">
            {busy ? 'Vaporizing…' : pos >= 1 ? 'Release to vaporize' : 'Slide to vaporize →'}
          </span>
          <button
            type="button"
            className="cs-vaporize-thumb"
            aria-label="Slide to vaporize"
            disabled={busy}
            onPointerDown={onPointerDown}
            style={{ left: `calc(3px + ${pos} * (100% - 52px))` }}
          >
            {busy ? '…' : '⏵'}
          </button>
        </div>
        {error && <div className="cs-empty" style={{ marginTop: 10 }}>{error}</div>}
        <div className="cs-vaporize-actions">
          <button type="button" className="cs-btn cs-btn-ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Combined delete controls for any media item. When active, shows a trash
// button (recoverable). When already trashed, shows Restore + Vaporize and how
// many days remain before automatic purge.
export function TrashControls({
  trashed,
  daysLeft,
  what = 'this item',
  onTrash,
  onRestore,
  onVaporize,
  compact = true,
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const run = (fn) => async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!trashed) {
    return (
      <button
        type="button"
        className="cs-btn cs-btn-ghost cs-btn-sm"
        title={`Move to trash · recoverable for ${TRASH_RETENTION_DAYS} days`}
        disabled={busy}
        onClick={run(onTrash)}
      >
        🗑{compact ? '' : ' Delete'}
      </button>
    );
  }

  return (
    <>
      <span className="cs-trash-controls" onClick={(e) => e.stopPropagation()}>
        {typeof daysLeft === 'number' && (
          <span className="cs-trash-days" title="Automatically purged after this">
            deletes in {daysLeft}d
          </span>
        )}
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={run(onRestore)}>
          Restore
        </button>
        <button
          type="button"
          className="cs-btn cs-btn-danger cs-btn-sm"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            setConfirming(true);
          }}
        >
          Vaporize
        </button>
      </span>
      {confirming && (
        <VaporizeModal
          what={what}
          onConfirm={onVaporize}
          onClose={() => setConfirming(false)}
        />
      )}
    </>
  );
}
